import { usePermissions } from '@shared/hooks/usePermissions';
import { isInvoicesDebugEnabled, isInvoicesDebugLogsEnabled } from '../config/debug';

/**
 * Hook de permisos específico para el módulo Invoices/Orders
 *
 * ⚠️ Incluye modo debug para desarrollo
 * Cuando INVOICES_DEBUG_CONFIG.PERMISSIONS está activo,
 * todas las verificaciones retornan true
 *
 * @example
 * ```tsx
 * const { canView, canCreatePreinvoices, canEditLines, canBlockLines } = useInvoicesPermissions();
 * ```
 */
export const useInvoicesPermissions = () => {
  const { canAccess, hasAnyPermission, teamType } = usePermissions();
  const debugMode = isInvoicesDebugEnabled();
  const debugLogs = isInvoicesDebugLogsEnabled();

  // 🧪 Log de debug si está habilitado
  if (debugLogs && typeof window !== 'undefined') {
    console.log('🧪 [EligibleShipments] useInvoicesPermissions', {
      location: '/invoices/eligible-shipments',
      teamType,
      debugMode,
      message: debugMode
        ? '✅ Modo debug activo - Todos los permisos habilitados'
        : '❌ Modo debug inactivo - Verificando permisos reales',
    });
  }

  /**
   * Wrapper para verificaciones de permisos con debug mode
   */
  const checkPermission = (_teamTypes: string[], permissions: string[], actionName: string): boolean => {
    if (debugMode) {
      if (debugLogs) {
        console.log(`🧪 [INVOICES] ${actionName} - Bypass activo, retornando true`);
      }
      return true;
    }

    // 🎯 Siguiendo la doc: Si tiene el permiso (slug), tiene el acceso
    const hasAccess = hasAnyPermission(permissions);

    if (debugLogs) {
      console.log(`🔒 [INVOICES] ${actionName}`, {
        permissions,
        result: hasAccess,
      });
    }

    return hasAccess;
  };

  return {
    /**
     * Permiso para ver la sección de Invoices/Orders
     */
    canView: checkPermission(['tenant_3pl', 'tenant_external'], ['invoices.view'], 'Ver sección'),

    /**
     * Permiso para crear prefacturas (botón Prefacturar)
     */
    canCreatePreinvoices: checkPermission(['tenant_3pl'], ['pre-invoices.create'], 'Crear prefacturas'),

    /**
     * Permiso para editar líneas de prefacturas
     */
    canEditLines: checkPermission(['tenant_3pl'], ['pre-invoices.edit'], 'Editar líneas'),

    /**
     * Permiso para bloquear shipments
     */
    canBlockLines: checkPermission(['tenant_3pl'], ['shipments.block'], 'Bloquear shipments'),

    /**
     * Permiso para desbloquear shipments
     */
    canRestoreLines: checkPermission(['tenant_3pl'], ['shipments.unblock'], 'Desbloquear shipments'),

    /**
     * Permiso para generar facturas a partir de prefacturas
     */
    canCreateInvoices: checkPermission(['tenant_3pl'], ['invoices.create'], 'Crear facturas'),

    /**
     * Flag para saber si estamos en modo debug
     */
    isDebugMode: debugMode,

    /**
     * Team type del usuario actual
     */
    teamType,
  };
};
